import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form, Field } from "formik";

const ReusableFormDialog = ({
  open,
  onClose,
  title,
  initialValues,
  validationSchema,
  fields,
  onSubmit,
  loading = false,
  children,
  maxWidth = "sm",
}) => {
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          pt: 2,
          px: 3,
          flexShrink: 0,
          background: "linear-gradient(135deg, #36589A 0%, #2E4D88 100%)",
          color: "#FFFFFF",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "200px",
            height: "200px",
            // background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "50%",
            transform: "translate(30%, -30%)",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1, fontWeight: 600 }}>
          {title}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "white",
            position: "relative",
            zIndex: 1,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ errors, touched, values, setFieldValue }) =>
          (() => {
            const resolvedFields =
              typeof fields === "function" ? fields(values) : fields;
            return (
              <Form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                <DialogContent
                  dividers
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#555",
                    },
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {resolvedFields.map((field) => {
                      if (field.type === "select") {
                        return (
                          <Field
                            key={field.name}
                            as={TextField}
                            select
                            fullWidth
                            name={field.name}
                            label={field.label}
                            error={
                              touched[field.name] && Boolean(errors[field.name])
                            }
                            helperText={
                              touched[field.name] && errors[field.name]
                            }
                            disabled={field.disabled || false}
                            size="small"
                          >
                            {field.options?.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Field>
                        );
                      }

                      if (field.type === "custom") {
                        return (
                          <Box key={field.name}>
                            {field.component({
                              values,
                              setFieldValue,
                              errors,
                              touched,
                            })}
                          </Box>
                        );
                      }

                      return (
                        <Field
                          key={field.name}
                          as={TextField}
                          fullWidth
                          name={field.name}
                          label={field.label}
                          type={field.type || "text"}
                          value={values[field.name] ?? ""}
                          placeholder={field.placeholder || ""}
                          onChange={(event) => {
                            const rawValue = event.target.value;
                            const nextValue = field.formatter
                              ? field.formatter(
                                  rawValue,
                                  values[field.name],
                                  values,
                                )
                              : rawValue;
                            setFieldValue(field.name, nextValue);
                          }}
                          error={
                            touched[field.name] && Boolean(errors[field.name])
                          }
                          helperText={
                            (touched[field.name] && errors[field.name]) ||
                            field.helperText ||
                            ""
                          }
                          disabled={field.disabled || false}
                          multiline={field.multiline || false}
                          rows={field.rows || 1}
                          size="small"
                          inputProps={field.inputProps || {}}
                          InputLabelProps={
                            field.type === "date" ? { shrink: true } : {}
                          }
                        />
                      );
                    })}
                  </Box>
                  {children}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, flexShrink: 0 }}>
                  <Button onClick={onClose} variant="outlined" color="error">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={20} />}
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      backgroundColor: "#36589A",
                      "&:hover": {
                        backgroundColor: "#2E4D88",
                      },
                    }}
                  >
                    {loading ? "Saving..." : "Save"}
                  </Button>
                </DialogActions>
              </Form>
            );
          })()
        }
      </Formik>
    </Dialog>
  );
};

export default ReusableFormDialog;
